import React from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { Block } from 'suomifi-ui-components'
import fpSome from 'lodash/fp/some'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import {
  isEidasIdentifiedUser,
  isHetuIdentifiedUser,
} from 'todistukset-common/src/vtj/user.utils'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  userBlockedReason,
  BlockingAnnouncement,
} from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockedAnnouncement.util'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import OrderFlowName from 'todistukset-ui/src/vtj/OrderFlowName.enum'

interface BlockedAnnouncementProps {
  certificateType: CertificateTypes
  orderFlowName?: OrderFlowName | undefined
}

const BlockedAnnouncements: React.FC<BlockedAnnouncementProps> = ({
  certificateType,
  orderFlowName = undefined,
}) => {
  const {
    common: { user },
  } = useStore()

  if (user && userBlockedReason(user, certificateType, orderFlowName)) {
    return <BlockingAnnouncement {...{ user, certificateType }} />
  }

  /**
   * These aren't for actually blocking the user, they're just misusing
   * the same UI slot for showing a notice in the ui.
   *
   * Probably the right thing to do would be to put these in suomifi
   * InlineAlert and show them inside the wizard flow in relevant places
   */
  if (user && user.isOver15 && !user.isAdult) {
    return (
      <AnnouncementInfoBlock
        title="under18RestrictionsTitle"
        paragraphs={['under18RestrictionsContent']}
        dataTestId="under-18-restrictions-announcement"
      />
    )
  }
  if (
    user &&
    certificateType === CertificateTypes.SYNTYMATODISTUS &&
    (isEidasIdentifiedUser(user) ||
      (isHetuIdentifiedUser(user) && !user.isBornInFinland) ||
      fpSome((dependant) => !dependant.isBornInFinland, user.huollettavat))
  ) {
    return (
      <AnnouncementInfoBlock
        title="notBornInFinlandTitle"
        paragraphs={['notBornInFinlandContent']}
        dataTestId="not-born-in-finland-announcement"
      />
    )
  }

  return <React.Fragment />
}

const AnnouncementInfoBlock: React.FC<{
  title?: string
  paragraphs?: string[]
  dataTestId?: string
}> = ({ title, paragraphs = [], dataTestId: testId = '' }) => {
  const { t } = useTranslation()
  // This uses the same structure as BlockedAnnouncement component.
  return (
    <Block pt={'xxl'} data-test-id={testId} type="info">
      {title && (
        <p>
          <strong>{t(title)}</strong>
        </p>
      )}
      {paragraphs.length && (
        <div>
          {paragraphs.map((i18nkey) => (
            <p key={i18nkey}>
              <LocalizedString stringKey={i18nkey} />
            </p>
          ))}
        </div>
      )}
    </Block>
  )
}

export default observer(BlockedAnnouncements)
